import React from 'react';

import type { Icon } from '@atlaskit/editor-common/extensions';
import type { NewCoreIconProps } from '@atlaskit/icon/base-new';

type AtlaskitIconComponent = React.ComponentType<NewCoreIconProps>;
type TemplateQuickInsertIcon = 'decision-register' | 'executive-summary' | 'meeting-recap';

/** Lazy-load the Rovo icon, sharing the chunk across all manifest icon entries. */
const loadAgentManagedExtensionIcon = (): Promise<{
	default: React.ComponentType<{ label: string; size?: 'small' | 'medium' | 'large' | 'xlarge' }>;
}> =>
	import(
		/* webpackChunkName: "@atlaskit-internal_editor-agent-managed-extension-icon" */
		'@atlassian/generative-ai-modal/assets/RovoIcon'
	);

const templateQuickInsertIconLoaders: Record<
	TemplateQuickInsertIcon,
	() => Promise<{ default: AtlaskitIconComponent }>
> = {
	'decision-register': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_editor-agent-managed-extension-decision-icon" */
			'@atlaskit/icon/core/decision'
		),
	'executive-summary': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_editor-agent-managed-extension-summary-icon" */
			'@atlaskit/icon/core/ai-generative-text-summary'
		),
	'meeting-recap': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_editor-agent-managed-extension-meeting-icon" */
			'@atlaskit/icon/core/pages'
		),
};

/** Build a manifest-shaped icon loader that renders the Rovo icon at the given size. */
export const getAgentManagedExtensionIconLoader =
	(label: string, size: 'small' | 'medium' = 'medium'): Icon =>
	() =>
		loadAgentManagedExtensionIcon().then((mod) => {
			const RovoIcon = mod.default;
			const IconForManifest = () => <RovoIcon label={label} size={size} />;
			IconForManifest.displayName = 'AgentManagedExtensionManifestIcon';
			return IconForManifest;
		});

export const getTemplateQuickInsertIconLoader =
	(label: string, icon: TemplateQuickInsertIcon): Icon =>
	() =>
		templateQuickInsertIconLoaders[icon]().then((mod) => {
			const TemplateIcon = mod.default;
			const IconForManifest = () => <TemplateIcon label={label} color="currentColor" />;
			IconForManifest.displayName = 'AgentManagedExtensionTemplateQuickInsertIcon';
			return IconForManifest;
		});
